import { useCallback } from 'react'
import { v7 as uuidv7 } from 'uuid'
import { compareRanks, generateRankBetween } from '@/lib/utils/lexorank'
import { buildPlainTextEditorStateJSON } from '@/components/editor/TiptapRichTextEditor'
import { getRxDB } from '@/lib/db/rxdb'
import {
  useCycleWorkItemMutations,
  useTaskContentMutations,
  useTaskLabelMutations,
  useTaskMutations,
  useTaskRelationMutations,
} from '@/lib/db/rxdb/hooks'
import { createTaskWithLabels } from '@/lib/sync/taskActions'
import { RELATION_TYPES } from '@/lib/types/taskRelation'
import { logger } from '@/lib/monitoring/logger'
import useLoginStore from '@/store/LoginStore'

export interface NewSubTask {
  name: string
  statusId: string
  /** Defaults to nobody; the inline row's "You" chip sets the current user. */
  userId?: string | null
  /** Unix ms, date only. */
  dueDate?: number | null
}

/**
 * Creates a sub-task under `parentId` (design 28b): last in order, inheriting
 * the parent's project and its open cycles, linked by a parent_child relation.
 * Shared by the inline add row and a checklist item's "Make sub-task".
 * Resolves to the new task's id, or null when nothing was created.
 */
export function useCreateSubTask(parentId: string | null | undefined, workspaceId: string | null | undefined) {
  const currentUserId = useLoginStore((s) => s.userId)
  const { createTask } = useTaskMutations()
  const { createTaskLabel } = useTaskLabelMutations()
  const { createTaskContent } = useTaskContentMutations()
  const { createTaskRelation } = useTaskRelationMutations()
  const { createCycleWorkItem } = useCycleWorkItemMutations()

  return useCallback(
    async ({ name, statusId, userId = null, dueDate = null }: NewSubTask): Promise<string | null> => {
      const trimmed = name.trim()
      if (!trimmed || !parentId || !workspaceId || !currentUserId || !statusId) return null

      const db = getRxDB()
      const parent = await db.tasks.findOne(parentId).exec()
      if (!parent || parent.isDeleted) return null

      const siblings = await db.tasks
        .find({ selector: { envId: workspaceId, isDeleted: false, parentId } })
        .exec()
      const lastRank = siblings.reduce<string | null>((max, doc) => {
        const rank = doc.sortOrder ?? null
        return rank && (!max || compareRanks(rank, max) > 0) ? rank : max
      }, null)

      const taskId = uuidv7()
      const editorJson = buildPlainTextEditorStateJSON('')
      await createTaskWithLabels({
        task: {
          id: taskId,
          name: trimmed,
          description: editorJson,
          descriptionFormat: 'tiptap',
          descriptionLength: editorJson.length,
          descriptionPlainText: null,
          sortOrder: generateRankBetween(lastRank, null),
          projectId: parent.projectId ?? null,
          userId,
          dueDateType: dueDate ? 'date' : null,
          dueDate: dueDate ?? null,
          priority: 0,
          isSomeday: false,
          isAllDay: true,
          statusId,
          parentId,
          statusName: '',
          statusColor: '#gray',
        },
        labelIds: [],
        createTask,
        createTaskLabel,
        createTaskContent,
      })

      await createTaskRelation({
        sourceTaskId: parentId,
        sourceProjectId: parent.projectId ?? null,
        sourceTaskName: parent.name,
        sourceIdentifier: parent.identifier ?? null,
        targetTaskId: taskId,
        targetProjectId: parent.projectId ?? null,
        targetTaskName: trimmed,
        targetIdentifier: null,
        relationType: RELATION_TYPES.PARENT_CHILD,
      })

      // Cycle membership lives in cycleworkitems, not on the task: copy the
      // parent's memberships in cycles that are still open.
      try {
        const memberships = await db.cycleworkitems
          .find({ selector: { envId: workspaceId, workItemId: parentId, isDeleted: false } })
          .exec()
        if (memberships.length > 0) {
          const cycles = await db.cycles.findByIds(memberships.map((m) => m.cycleId)).exec()
          for (const membership of memberships) {
            const cycle = cycles.get(membership.cycleId)
            if (!cycle || cycle.isDeleted || cycle.status === 'completed') continue
            await createCycleWorkItem({
              envId: workspaceId,
              cycleId: membership.cycleId,
              workItemId: taskId,
              statusIdAtClose: null,
              progressTypeAtClose: null,
              addedAt: Date.now(),
              sourceCycleId: null,
            })
          }
        }
      } catch (error) {
        logger.logError(error as Error, 'DATABASE', { operation: 'sub_task_inherit_cycle', taskId })
      }

      return taskId
    },
    [
      parentId,
      workspaceId,
      currentUserId,
      createTask,
      createTaskLabel,
      createTaskContent,
      createTaskRelation,
      createCycleWorkItem,
    ],
  )
}
